import { useEffect, useState } from "react";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import TemplatesService from '../Services/TemplatesService';
import PubSub from 'pubsub-js';

function ApplyTemplateModal({ show, onClose, templateId, templateName }) {
    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaigns, setSelectedCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (show && templateId) {
            getCampaigns();
        }
    }, [show, templateId]);

    async function getCampaigns() {
        setLoading(true);
        try {
            const data = await TemplatesService.getCampaignsForTemplateApply(templateId);
            setCampaigns(data);
        } catch (error) {
            console.error('Error getting campaigns:', error);
            PubSub.publish('show-basic-notification', {
                title: 'Error',
                message: 'Error loading campaigns'
            });
        } finally {
            setLoading(false);
        }
    }

    function handleCampaignSelect(campaignId) {
        setSelectedCampaigns(prev => {
            if (prev.includes(campaignId)) {
                return prev.filter(id => id !== campaignId);
            } else {
                return [...prev, campaignId];
            }
        });
    }

    function handleSelectAll() {
        if (selectedCampaigns.length === filteredCampaigns.length) {
            setSelectedCampaigns([]);
        } else {
            setSelectedCampaigns(filteredCampaigns.map(c => c._id));
        }
    }

    async function handleApplyTemplate() {
        if (selectedCampaigns.length === 0) {
            PubSub.publish('show-basic-notification', {
                title: 'Warning',
                message: 'Please select at least one campaign'
            });
            return;
        }

        setApplying(true);
        try {
            for (const campaignId of selectedCampaigns) {
                await TemplatesService.applyTemplateToCampaign(templateId, campaignId);
            }

            PubSub.publish('show-basic-notification', {
                title: 'Success',
                message: `Template applied successfully to ${selectedCampaigns.length} campaign(s)`
            });

            onClose();
            setSelectedCampaigns([]);
        } catch (error) {
            console.error('Error applying template:', error);
            PubSub.publish('show-basic-notification', {
                title: 'Error',
                message: 'Error applying template to campaigns'
            });
        } finally {
            setApplying(false);
        }
    }

    const filteredCampaigns = campaigns.filter(campaign =>
        campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.user?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.user?.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Modal show={show} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Apply Template to Campaigns</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <h6>Template: <span className="text-primary">{templateName}</span></h6>
                    <p className="text-muted small">
                        This will overwrite the content of the selected campaigns' steps (HTML, text, subject) 
                        while keeping their configurations (leads, dates, status).
                    </p>
                </div>

                <div className="mb-3">
                    <Form.Control
                        type="text"
                        placeholder="Search campaigns by name, user name, or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="small text-muted">
                            {filteredCampaigns.length} campaigns found
                        </span>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleSelectAll}
                        >
                            {selectedCampaigns.length === filteredCampaigns.length ? 'Deselect All' : 'Select All'}
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="campaign-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {filteredCampaigns.map((campaign) => (
                            <div key={campaign._id} className="card mb-2">
                                <div className="card-body py-2">
                                    <div className="d-flex align-items-center">
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedCampaigns.includes(campaign._id)}
                                            onChange={() => handleCampaignSelect(campaign._id)}
                                            className="me-3"
                                        />
                                        <div className="flex-grow-1">
                                            <h6 className="mb-1">{campaign.name}</h6>
                                            <div className="small text-muted">
                                                <span className="me-3">
                                                    <i className="fas fa-user me-1"></i>
                                                    {campaign.user?.name} ({campaign.user?.email})
                                                </span>
                                                <span className="me-3">
                                                    <i className="fas fa-list me-1"></i>
                                                    {campaign.leads_id?.length || 0} leads
                                                </span>
                                                <span className={`badge ${campaign.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                    {campaign.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {selectedCampaigns.length > 0 && (
                    <div className="alert alert-warning mt-3">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>Warning:</strong> This will overwrite the content of {selectedCampaigns.length} campaign(s). 
                        The changes cannot be undone.
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleApplyTemplate}
                    disabled={selectedCampaigns.length === 0 || applying}
                >
                    {applying ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Applying...
                        </>
                    ) : (
                        `Apply to ${selectedCampaigns.length} Campaign(s)`
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ApplyTemplateModal;
